/**
 * Upload guards — detect "the file changed on disk after it was selected" and
 * turn the browser's opaque failure into a clear, actionable, localized error.
 *
 * The problem: when a user picks a file and then moves/edits/deletes it before
 * the upload sends, Chrome aborts the request with ``net::ERR_UPLOAD_FILE_CHANGED``
 * and ``fetch()`` rejects with a bare ``TypeError: Failed to fetch``. That bubbles
 * up to a generic toast AND — for create-then-upload flows — leaves an empty
 * entity (design/product/piece) behind, because the parent row was already
 * created before the upload failed.
 *
 * The fix has two layers, both built on the same primitive:
 *   1. ``ensureFileReadable`` — a cheap pre-flight: read a small head slice to
 *      force the browser to validate the File snapshot against the file on disk.
 *      If it changed, the read rejects and we throw a ``FileChangedError`` BEFORE
 *      any network call or DB write. Call it at the top of a save handler to
 *      abort the whole save (so nothing empty is persisted).
 *   2. ``safeUploadFetch`` — a drop-in ``fetch`` wrapper for the actual upload
 *      that pre-checks readability and maps the opaque network ``TypeError`` to a
 *      ``FileChangedError`` (covers a file that changes between the pre-flight
 *      and the send, and any genuine connection drop mid-upload).
 *
 * Framework-agnostic (used by tenant + admin + shared components). The user
 * message comes from the ``fileValidation`` label registry, so it is localized
 * in the tenant and Spanish in the admin without any prop drilling.
 */
import { getFileChangedLabel } from './fileValidation';

/** Bytes pre-read to trigger the browser's File-snapshot validation. A head
 *  slice is enough: the browser checks the snapshot (size + last-modified) on
 *  ANY read, so we never buffer a multi-GB STL just to detect a change. */
const READABILITY_PROBE_BYTES = 64 * 1024;

/** Thrown when a selected file can no longer be read at upload time because it
 *  was moved/edited/deleted after selection (Chrome: ERR_UPLOAD_FILE_CHANGED),
 *  or when a network-level failure aborts an in-flight upload. Carries a ready
 *  localized ``message`` so call sites can toast it directly. */
export class FileChangedError extends Error {
  readonly code = 'file_changed' as const;
  readonly fileName: string;
  constructor(fileName = '', options?: { cause?: unknown }) {
    super(getFileChangedLabel(fileName));
    this.name = 'FileChangedError';
    this.fileName = fileName;
    // `cause` isn't in older lib targets — assign defensively.
    if (options && 'cause' in options) (this as { cause?: unknown }).cause = options.cause;
  }
}

/** Type guard — true for a ``FileChangedError`` (instanceof or by ``code``, so it
 *  survives bundling boundaries between apps). */
export function isFileChangedError(err: unknown): err is FileChangedError {
  return (
    err instanceof FileChangedError ||
    (typeof err === 'object' && err !== null && (err as { code?: unknown }).code === 'file_changed')
  );
}

/** ``fetch()`` rejects with a ``TypeError`` only for network-level failures
 *  (DNS, connection drop, CORS, ERR_UPLOAD_FILE_CHANGED) — never for HTTP error
 *  statuses (those resolve with ``res.ok === false``). So a TypeError thrown
 *  while uploading a file is, in practice, "the file changed or the connection
 *  dropped" — both of which our message tells the user to retry. */
function isOpaqueNetworkFailure(err: unknown): boolean {
  return err instanceof TypeError;
}

/**
 * Read a small head slice so the browser validates the File snapshot against
 * the file currently on disk. If the file was modified/moved/removed after
 * selection, the read rejects (``NotReadableError``) and we throw a
 * ``FileChangedError`` — a clear, actionable error raised BEFORE any network
 * call or entity creation.
 *
 * No-op for non-``File`` blobs (in-memory data has no on-disk snapshot to
 * invalidate) and in non-browser environments.
 */
export async function ensureFileReadable(file: File | Blob): Promise<void> {
  if (typeof File === 'undefined' || !(file instanceof File)) return;
  try {
    const end = Math.min(file.size || 0, READABILITY_PROBE_BYTES);
    // slice(0, 0) on a 0-byte file still resolves; that's fine — a genuinely
    // unreadable empty file is caught by safeUploadFetch's fallback instead.
    await file.slice(0, end).arrayBuffer();
  } catch (cause) {
    throw new FileChangedError(file.name, { cause });
  }
}

/**
 * Pre-flight a batch of files. Throws the FIRST ``FileChangedError`` so a save
 * handler can abort the entire operation before creating anything. ``null`` /
 * ``undefined`` entries are skipped (convenient for ``maybeFile`` lists).
 */
export async function ensureFilesReadable(
  files: Iterable<File | Blob | null | undefined>,
): Promise<void> {
  for (const f of files) {
    if (f) await ensureFileReadable(f);
  }
}

/**
 * Normalize a thrown upload error: returns the same ``FileChangedError`` when it
 * already is one, wraps an opaque network ``TypeError`` into a
 * ``FileChangedError`` (so the user gets the actionable message), and returns
 * any other error unchanged (HTTP errors keep their parsed detail).
 */
export function toUploadError(err: unknown, file?: { name?: string } | null): unknown {
  if (isFileChangedError(err)) return err;
  if (isOpaqueNetworkFailure(err)) return new FileChangedError(file?.name ?? '', { cause: err });
  return err;
}

/**
 * Drop-in ``fetch`` replacement for file uploads. Pre-checks readability (when a
 * ``File`` is passed), performs the request, and converts the opaque network
 * ``TypeError`` into a ``FileChangedError``. HTTP error statuses are returned as
 * a normal ``Response`` (``res.ok === false``) — the caller still handles those.
 */
export async function safeUploadFetch(
  input: RequestInfo | URL,
  init: RequestInit,
  file?: File | Blob | null,
): Promise<Response> {
  if (file) await ensureFileReadable(file);
  try {
    return await fetch(input, init);
  } catch (err) {
    throw toUploadError(err, file instanceof File ? file : null);
  }
}
